import { useState, useEffect } from 'react';

// Material UI
import { styled, Box, Typography, CircularProgress, Chip, Paper } from '@mui/material';
import { Card, CardHeader, CardContent, CardActions } from '@mui/material';
import { Table, TableBody, TableRow, TableCell } from '@mui/material';
import { Alert } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LaunchIcon from '@mui/icons-material/Launch';

import { ActionButton } from 'components/ui/buttons';

// Utility
import moment from 'moment-timezone';
import fetchExpresso from 'utility/fetchExpresso';
import { useAuth } from 'context/auth';
import { useHistory } from 'react-router-dom';

export default function StripeConnect() {
    const [state, setState] = useState({
        status: 'loading', // loading, error, success
        account: null,
        standardAuthorizationUrl: null,
    });

    const { auth } = useAuth();

    useEffect(() => {
        (async () => {
            try {
                const accountRequest = await fetchExpresso(
                    `/apiv2/hosts/${auth.hostId}/connect_status`,
                );

                if (accountRequest.status === 200) {
                    const { connect } = await accountRequest.json();

                    if (connect) {
                        setState((s) => ({ ...s, status: 'success', account: connect }));
                    } else {
                        setState((s) => ({ ...s, status: 'success', account: null }));
                    }
                } else {
                    throw new Error();
                }
            } catch (error) {
                setState((s) => ({ ...s, status: 'error', account: null }));
            }
        })();
    }, [auth.hostId, auth.admin]);

    useEffect(() => {
        if (state.account === 'standard_pending') {
            fetchExpresso(`/apiv2/hosts/${auth.hostId}/connect/standard/authorization_link`, {
                method: 'POST',
                body: {
                    redirectUri: `${process.env.REACT_APP_API_URL}/apiv2/hosts/${auth.hostId}/connect/standard/oauth_callback`,
                },
            }).then(async (res) => {
                const jsonData = await res.json();
                setState((s) => ({ ...s, standardAuthorizationUrl: jsonData.authorizationUrl }));
            });
        }
    }, [state.account, auth.hostId]);

    if (state.status === 'loading') {
        return (
            <Card>
                <CardHeader title="Payment Processing" />
                <CardContent>
                    <Box
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        height={150}
                    >
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (state.status === 'error') {
        return (
            <Card>
                <CardHeader title="Payment Processing" />
                <CardContent>
                    <Box
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        height={150}
                    >
                        <Typography>
                            Something went wrong. Please try refreshing the page.
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // ***** User is on Standard Stripe ***** //

    if (state.account === 'standard_pending' || state.account?.type === 'standard') {
        return (
            <StripePaymentsWidget
                account={state.account}
                standardAuthorizationUrl={state.standardAuthorizationUrl}
            />
        );
    }

    // ***** User is on Legacy Payments System ***** //

    if (state.account === null) {
        return <LegacyPaymentsWidget />;
    }

    // ***** User is on Stripe Connect ***** //

    return <FrontdoorPaymentsWidget account={state.account} />;
}

function LegacyPaymentsWidget() {
    return (
        <Card>
            <CardHeader title="Payment Processing" />
            <CardContent>
                <Option>
                    <Box display="flex" alignItems={'center'}>
                        <OptionName>FrontDoor+ Payments</OptionName>
                        <OptionCaption>(powered by Stripe)</OptionCaption>
                    </Box>
                    <Box ml={2} mt={1}>
                        <Typography>• Accept on-site payments</Typography>
                        <Typography>• More payout options</Typography>
                        <Box height={4} />
                        <ContactMessage>Contact us to learn more</ContactMessage>
                    </Box>
                </Option>

                <Box height={12} />

                <Option>
                    <Box display="flex" alignItems={'center'}>
                        <Check />
                        <OptionName>Legacy Payments</OptionName>
                    </Box>
                    <Box ml={2} mt={1}>
                        <Typography>• On-site payments not supported</Typography>
                        <Typography>• Payouts processed manually</Typography>
                    </Box>
                </Option>
            </CardContent>
        </Card>
    );
}

function FrontdoorPaymentsWidget(props) {
    const { account } = props;

    const history = useHistory();

    const {
        onboarding_complete,
        requirements_due,
        in_review,
        current_deadline,
        hosted_onboarding,
        hosted_onboarding_url,
        express_dashboard_url,
        charges_enabled,
        payouts_enabled,
    } = account;

    const setupRequired =
        onboarding_complete === false || requirements_due === true || in_review === true;

    return (
        <Card>
            <CardHeader
                title="FrontDoor+ Payments Setup"
                subheader={
                    current_deadline
                        ? 'Submission Due: ' +
                          moment(current_deadline * 1000).format('MMM DD, YYYY')
                        : undefined
                }
            />
            <CardContent>
                <ConnectStatusAlert
                    onboarding={onboarding_complete === false}
                    requirementsDue={requirements_due === true}
                    inReview={in_review === true}
                />
                {onboarding_complete === true && (
                    <Table>
                        <TableBody>
                            <Status title="Payments" enabled={charges_enabled} />
                            <Status title="Payouts" enabled={payouts_enabled} />
                        </TableBody>
                    </Table>
                )}
            </CardContent>
            <CardActions>
                {!onboarding_complete && (
                    <ActionButton
                        target="_blank"
                        rel="noopener noreferrer"
                        href={
                            'https://hub.frontdoor.plus/blog/understanding-the-updated-fintrac-and-kyc-requirements'
                        }
                        endIcon={<LaunchIcon />}
                    >
                        Read More
                    </ActionButton>
                )}
                {hosted_onboarding ? (
                    <ActionButton
                        target="_blank"
                        rel="noopener noreferrer"
                        href={hosted_onboarding_url}
                        endIcon={<LaunchIcon />}
                    >
                        {setupRequired ? 'Complete Setup' : 'Review'}
                    </ActionButton>
                ) : (
                    <ActionButton onClick={() => history.push('/settings/onboarding')}>
                        {setupRequired ? 'Complete Setup' : 'Review'}
                    </ActionButton>
                )}
                {express_dashboard_url ? (
                    <ActionButton
                        target="_blank"
                        rel="noopener noreferrer"
                        href={express_dashboard_url}
                        endIcon={<LaunchIcon />}
                    >
                        View Dashboard
                    </ActionButton>
                ) : null}
            </CardActions>
        </Card>
    );
}

function StripePaymentsWidget(props) {
    const { account, standardAuthorizationUrl } = props;

    // *** No Account Connected *** //

    if (account === 'standard_pending') {
        return (
            <Card>
                <CardHeader title="Payment Processing" />
                <CardContent>
                    <Typography>
                        You are almost ready to sell tickets. Connect your Stripe account to
                        FrontDoor+ so your ticket revenue goes directly into your Stripe account.
                    </Typography>

                    <Typography sx={{ fontStyle: 'italic', marginTop: 2 }}>
                        You will be redirected to Stripe to finish the setup.
                    </Typography>
                </CardContent>
                <CardActions>
                    <ActionButton
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={!standardAuthorizationUrl}
                        href={standardAuthorizationUrl}
                        endIcon={<LaunchIcon />}
                    >
                        Connect Stripe
                    </ActionButton>
                </CardActions>
            </Card>
        );
    }

    // *** Account Connected *** //

    const {
        id,
        onboarding_complete,
        requirements_due,
        in_review,
        current_deadline,
        hosted_onboarding_url,
        payouts_enabled,
        charges_enabled,
    } = account;

    const setupRequired =
        onboarding_complete === false || requirements_due === true || in_review === true;

    return (
        <Card>
            <CardHeader
                title="Payment Processing"
                subheader={
                    current_deadline
                        ? 'Submission Due: ' +
                          moment(current_deadline * 1000).format('MMM DD, YYYY')
                        : undefined
                }
            />
            <CardContent>
                <StandardConnectStatusAlert
                    onboarding={onboarding_complete === false}
                    requirementsDue={requirements_due === true}
                    inReview={in_review === true}
                />
                {onboarding_complete === true && (
                    <Table>
                        <TableBody>
                            <Status title="Payments" enabled={charges_enabled} />
                            <Status title="Payouts" enabled={payouts_enabled} />
                        </TableBody>
                    </Table>
                )}
            </CardContent>
            <CardActions>
                {setupRequired && (
                    <ActionButton
                        target="_blank"
                        rel="noopener noreferrer"
                        href={hosted_onboarding_url}
                        endIcon={<LaunchIcon />}
                    >
                        Complete Setup
                    </ActionButton>
                )}
                {onboarding_complete === true && (
                    <ActionButton
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`https://dashboard.stripe.com/${id}`}
                        endIcon={<LaunchIcon />}
                    >
                        Stripe Dashboard
                    </ActionButton>
                )}
            </CardActions>
        </Card>
    );
}

function ConnectStatusAlert(props) {
    const { onboarding, requirementsDue, inReview } = props;

    if (inReview) {
        return (
            <Box mb={4}>
                <Alert severity="info">
                    Stripe is processing your submission. Please come back later.
                </Alert>
            </Box>
        );
    }

    if (onboarding) {
        return (
            <Box mb={4}>
                <Box mb={4}>
                    <Alert severity="warning">
                        Before you can start accepting online payments, you'll need to complete a
                        brief setup process with our secure payment partner. This is required by
                        updated Canadian regulations to help protect against fraud and money
                        laundering. We know it's a bit more paperwork, but it keeps everything
                        running smoothly.
                    </Alert>
                </Box>

                <Typography>What You'll Need</Typography>

                <Typography component="ul" style={{ marginTop: 8 }}>
                    <Typography component={'li'}>
                        Your organization's registered name, business number and address (as shown
                        in your corporate registry).
                    </Typography>
                    <Typography component={'li'}>
                        Current list of directors (as shown in your corporate registry, we have a
                        Directors Update template if needed).
                    </Typography>
                    <Typography component={'li'}>Banking information</Typography>
                </Typography>

                <Typography style={{ marginTop: 20 }}>
                    While you fill out these details, we may receive status updates faster than you
                    do. That's completely normal. We'll let you know if there's anything you need to
                    do next.
                </Typography>

                <Typography style={{ marginTop: 20 }}>
                    When you're ready, click “Complete Setup” to begin. If you have any questions
                    along the way, just let us know. We're here to help!
                </Typography>
            </Box>
        );
    }

    if (requirementsDue) {
        return (
            <Box mb={4}>
                <Alert severity="error">
                    There are problems with your payment account that may impact your ability to
                    take payments and/or receive your money. Click "Complete Setup" to update your
                    profile and upload any required documents.
                </Alert>
            </Box>
        );
    }

    return (
        <Box mb={4}>
            <Typography>
                You are enrolled in FrontDoor+ Payments (powered by Stripe). You are good to sell
                tickets, and will receive payouts to your linked bank account.
            </Typography>
        </Box>
    );
}

function StandardConnectStatusAlert(props) {
    const { onboarding, requirementsDue, inReview } = props;

    if (inReview) {
        return (
            <Box mb={4}>
                <Alert severity="info">
                    Stripe is reviewing your account. This usually only takes a few minutes. You
                    will be able to accept payments and receive payouts once the review is complete.
                    You can keep working on your event while you wait.
                </Alert>
            </Box>
        );
    }

    if (onboarding) {
        return (
            <Box mb={4}>
                <Alert severity="warning">
                    It looks like your Stripe setup is not finished yet. Please complete your
                    business and payout details in Stripe so you can start accepting payments.
                </Alert>
            </Box>
        );
    }

    if (requirementsDue) {
        return (
            <Box mb={4}>
                <Alert severity="error">
                    There are a few things in Stripe that need your attention. Please review them in
                    your Stripe dashboard so your payments continue without interruption. We are
                    here if you need a hand.
                </Alert>
            </Box>
        );
    }

    return (
        <Box mb={4}>
            <Typography>
                Your Stripe account is connected and ready. You can now accept payments and receive
                payouts.
            </Typography>
        </Box>
    );
}

function Status(props) {
    const { enabled, title } = props;

    return (
        <TableRow>
            <TableCell width={8}>
                <Box height={'100%'} display="flex" alignItems="center">
                    {enabled ? <Check /> : <Cross />}
                </Box>
            </TableCell>
            <TableCell>
                <StatusHeader>{title}</StatusHeader>
            </TableCell>
            <TableCell align="right">
                {enabled ? <SuccessText>Enabled</SuccessText> : <ErrorText>Disabled</ErrorText>}
            </TableCell>
        </TableRow>
    );
}

const Option = styled(Paper)(({ theme }) => ({
    border: '1px solid',
    borderRadius: theme.spacing(2),
    borderColor: theme.palette.grey[400],
    backgroundColor: theme.palette.grey[200],
    padding: theme.spacing(2),
}));
Option.defaultProps = { elevation: 0 };

const OptionName = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: 18,
}));

const OptionCaption = styled(Typography)(({ theme }) => ({
    fontSize: 14,
    marginLeft: theme.spacing(1),
    paddingBottom: 2,
}));

const ContactMessage = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}));

const SuccessChip = styled(Chip)(({ theme }) => ({
    backgroundColor: theme.palette.success.main,
    borderColor: theme.palette.success.main,
    color: theme.palette.common.white,
    marginLeft: 'auto',
}));
SuccessChip.defaultProps = { size: 'small' };

const WarningChip = styled(Chip)(({ theme }) => ({
    backgroundColor: theme.palette.warning.main,
    borderColor: theme.palette.warning.main,
    color: theme.palette.common.white,
    marginLeft: 'auto',
}));
WarningChip.defaultProps = { size: 'small' };

const StatusHeader = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}));

const Check = styled(CheckCircleOutlinedIcon)(({ theme }) => ({
    color: theme.palette.success.main,
    marginRight: 4,
}));

const Cross = styled(CancelOutlinedIcon)(({ theme }) => ({
    color: theme.palette.error.main,
    marginRight: 4,
}));

const SuccessText = styled(Typography)(({ theme }) => ({
    color: theme.palette.success.main,
    fontWeight: 'bold',
}));

const ErrorText = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.main,
    fontWeight: 'bold',
}));
